import { RoundTitle } from "@client/components/round/RoundTitle";
import ThemeSelector from "@client/components/utils/ThemeSelector";
import CloudAlertOutline from "@client/icons/CloudAlertOutline";
import CloudDoneOutline from "@client/icons/CloudDoneOutline";
import CloudOffOutline from "@client/icons/CloudOffOutline";
import CloudSyncOutline from "@client/icons/CloudSyncOutline";
import DownChevron from "@client/icons/DownChevron";
import { ShowEventLogItem } from "@client/pages/round/logs/ShowEventLogItem";
import { StateContext } from "@client/pages/round/Round";
import {
  accessiblePhases,
  allPhases,
  getRoundStatusDisplayName,
} from "@client/utils/roundStatusUtils";
import userIsAdmin from "@client/utils/userIsAdmin";
import type { ActionMessage, UnconfirmedActionMessage } from "@engine/core";
import {
  getActiveEvaluators,
  getActiveFunders,
  getActiveRecommendersWithTrack,
  getEffectiveTrack,
  getTracksToShow,
  isUserFunder,
  isUserRecommenderOrFunder,
} from "@engine/round/accessors";
import type { RoundAggregate, TrackStatus } from "@engine/round/aggregate";
import { trackNames, type Track } from "@lib/types";
import {
  anonymizeTrackMember,
  canView,
  makeTrackMembers,
} from "@lib/utils/anonymization";
import { assertUnreachable } from "@lib/utils/assertUnreachable";
import { sortByMultipleProperties } from "@lib/utils/sortByMultipleProperties";
import { sortByProperty } from "@lib/utils/sortByProperty";
import * as Sentry from "@sentry/react";
import clsx from "clsx";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import type { Socket as IoSocket } from "socket.io-client";
import { Link, useLocation } from "wouter";
import { Button } from "../utils/Button";
import Modal from "../utils/Modal";
import { Tooltip } from "../utils/Tooltip";

type NavItemVisibility = {
  visible:
    | "visible"
    | "disabled-for-non-admins"
    | "hidden-from-non-admins"
    | "hidden-for-all";
  tooltip?: string;
};

// Define a proper type for NavItem
type NavItem = {
  label: string;
  href?: string;
  pathPatterns: string[];
  items?: {
    label: string;
    href?: string;
    isSubmenu?: boolean;
    subitems?: { label: string; href: string }[];
  }[];
  canAccess: (
    round: RoundAggregate,
    userId: string,
    effectiveTrack: Track,
  ) => NavItemVisibility;
};

type NavItemAccess = {
  adminOnly?: boolean;
  isEvaluator?: boolean;
  roundNameRestriction?: string;
  phases?: TrackStatus[];
};

function joinWithAnd(items: string[]): string {
  if (items.length === 0) {
    return "";
  }
  return items.slice(0, -1).join(", ") + ", and " + items[items.length - 1];
}

function phaseRestrictedTooltip(
  phases: TrackStatus[],
  currentPhases: TrackStatus[],
): string {
  if (phases.length === 0) {
    return "This section is not available in any phase";
  }

  const earliestAccessiblePhase = phases.reduce((earliest, phase) =>
    parseInt(phase[0]) < parseInt(earliest[0]) ? phase : earliest,
  );

  if (
    currentPhases.every(
      (currentPhase) =>
        parseInt(currentPhase[0]) < parseInt(earliestAccessiblePhase[0]),
    )
  ) {
    return `This section will be available starting in the ${getRoundStatusDisplayName(earliestAccessiblePhase)} phase.`;
  }

  const latestAccessiblePhase = phases.reduce((latest, phase) =>
    parseInt(phase[0]) > parseInt(latest[0]) ? phase : latest,
  );

  if (
    currentPhases.every(
      (currentPhase) =>
        parseInt(currentPhase[0]) > parseInt(latestAccessiblePhase[0]),
    )
  ) {
    return `This section was only available through the end of the ${getRoundStatusDisplayName(latestAccessiblePhase)} phase.`;
  }

  // If the current phases aren't all on one side of the accessible phases,
  // then we should just list the available phases.

  return `This section is only available in the ${joinWithAnd(
    phases.map((p) => getRoundStatusDisplayName(p)),
  )} phases`;
}

// NavItemLink component for consistent link styling based on hierarchy level
function NavItemLink({
  href,
  selected,
  hierarchyLevel,
  label,
  disabled,
}: {
  href: string;
  selected: boolean;
  hierarchyLevel: 1 | 2 | 3;
  label: string;
  disabled?: boolean;
}) {
  // Define styles based on hierarchy level
  const getLevelStyles = () => {
    switch (hierarchyLevel) {
      case 1: // Top level navigation
        return "block w-full px-4 py-2.5 font-medium";
      case 2: // Submenu item
        return "block px-3 py-1.5 text-sm";
      case 3: // Sub-submenu item
        return "block px-3 py-1.5 text-sm";
      default:
        return "";
    }
  };

  return (
    <Link
      href={disabled ? "#" : href}
      className={clsx(
        getLevelStyles(),
        "rounded-md transition-colors duration-150",
        hierarchyLevel === 1
          ? "hover:bg-stone-300 dark:hover:bg-stone-700"
          : "hover:bg-stone-300 dark:hover:bg-stone-600",
        selected &&
          "border border-blue-800 dark:border-blue-500 text-blue-800 dark:text-blue-500",
        disabled && "opacity-50 cursor-not-allowed",
      )}
      onClick={(e) => {
        if (disabled) {
          e.preventDefault();
        }
      }}
    >
      {label}
    </Link>
  );
}

// NavItemMenu component for consistent menu styling and behavior
function NavItemMenu({
  label,
  hierarchyLevel,
  canToggle,
  isOpen,
  onToggle,
  hasActiveChild,
  disabled,
  children,
  tooltip,
}: {
  label: string;
  hierarchyLevel: 1 | 2 | 3;
  canToggle: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  hasActiveChild?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  tooltip?: string;
}) {
  // Define styles based on hierarchy level
  const getLevelStyles = () => {
    switch (hierarchyLevel) {
      case 1: // Top level menu
        return "px-4 py-2.5 font-medium";
      case 2: // Submenu
        return "px-3 py-1.5 text-sm font-medium";
      case 3: // Sub-submenu
        return "px-3 py-1.5 text-sm font-medium";
      default:
        return "";
    }
  };

  const getHoverStyles = () => {
    return hierarchyLevel === 1
      ? "hover:bg-stone-300 dark:hover:bg-stone-700"
      : "hover:bg-stone-300 dark:hover:bg-stone-600";
  };

  return (
    <div>
      <Tooltip title={tooltip}>
        {!disabled && canToggle ? (
          <button
            onClick={onToggle}
            className={clsx(
              "w-full text-left",
              getLevelStyles(),
              "rounded-md transition-colors duration-150",
              getHoverStyles(),
              "flex items-center justify-between",
              isOpen && "font-semibold",
              !isOpen && hasActiveChild && "text-blue-800 dark:text-blue-500",
            )}
          >
            <span>{label}</span>
            <DownChevron
              className={clsx(
                "w-4 h-4 fill-current transform transition-transform",
                isOpen && "rotate-180",
              )}
            />
          </button>
        ) : (
          <div
            className={clsx(
              "w-full text-left",
              getLevelStyles(),
              "rounded-md",
              hasActiveChild && "text-blue-800 dark:text-blue-500",
              disabled && "opacity-50 cursor-not-allowed",
            )}
          >
            {label}
          </div>
        )}
      </Tooltip>
      {isOpen && children && (
        <div
          className={clsx(
            hierarchyLevel === 1
              ? "pl-4 mt-1 mb-2 space-y-1"
              : "ml-3 pl-2 mt-1 mb-1 space-y-1",
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// Generic type for items that may have children
type NavItemType = {
  label: string;
  href?: string;
  isSubmenu?: boolean;
  subitems?: NavItemType[];
};

// Main navigation item component with recursive rendering
function NavigationItem({
  label,
  href,
  items,
  roundId,
  activeSubmenu,
  setActiveSubmenu,
  visibility,
  isAdmin,
}: {
  label: string;
  href?: string;
  items?: NavItemType[];
  roundId: string;
  activeSubmenu: string | null;
  setActiveSubmenu: (menu: string | null) => void;
  visibility: NavItemVisibility;
  isAdmin: boolean;
}) {
  const [location] = useLocation();

  // Helper function to check if a location matches this path
  const isPathActive = (path: string) => {
    // Extract the part of the path after roundId
    const normalizedPath = path.replace(`~/round/${roundId}`, "");

    // Extract the path segment from the current location
    const currentPath = location.startsWith("/round/")
      ? "/" + location.split("/", 3).slice(2).join("/")
      : location;

    // Check for exact match or if current path starts with this path + '/'
    return (
      currentPath === normalizedPath ||
      currentPath.startsWith(normalizedPath + "/")
    );
  };

  // Create a function to check if an item or any of its children are active
  const hasActiveItems = (navItems?: NavItemType[]): boolean => {
    if (!navItems) return false;

    return navItems.some(
      (item) =>
        (item.href && isPathActive(item.href)) || hasActiveItems(item.subitems),
    );
  };

  // Recursive function to render navigation items
  const renderNavItem = (
    item: NavItemType,
    level: 1 | 2 | 3,
    index: number,
  ): React.ReactNode => {
    // Format the label based on admin visibility
    const itemLabel =
      isAdmin &&
      (visibility.visible === "hidden-from-non-admins" ||
        visibility.visible === "disabled-for-non-admins") &&
      level === 1
        ? `(${item.label})`
        : item.label;

    // A. If it's a link
    if (item.href) {
      return (
        <Tooltip key={index} title={visibility.tooltip}>
          <NavItemLink
            href={item.href}
            selected={isPathActive(item.href)}
            hierarchyLevel={level}
            label={itemLabel}
            disabled={
              visibility.visible === "disabled-for-non-admins" && !isAdmin
            }
          />
        </Tooltip>
      );
    }

    // B. If it's a submenu with children
    if (item.isSubmenu || item.subitems) {
      const hasActiveChildren = hasActiveItems(item.subitems);
      const isToggleable = level === 1;
      const isOpen = level === 1 ? activeSubmenu === item.label : true;

      return (
        <NavItemMenu
          key={index}
          label={itemLabel}
          hierarchyLevel={level}
          canToggle={isToggleable}
          isOpen={isOpen}
          onToggle={
            isToggleable
              ? () =>
                  level === 1 && setActiveSubmenu(isOpen ? null : item.label)
              : undefined
          }
          hasActiveChild={hasActiveChildren}
          disabled={
            visibility.visible === "disabled-for-non-admins" && !isAdmin
          }
          tooltip={visibility.tooltip}
        >
          {item.subitems?.map((subitem, subIndex) => {
            const nextLevel = Math.min(level + 1, 3) as 1 | 2 | 3;
            return renderNavItem(subitem, nextLevel, subIndex);
          })}
        </NavItemMenu>
      );
    }

    // C. If it's just a label (no link or children)
    return (
      <Tooltip title={visibility.tooltip}>
        <div
          key={index}
          className={clsx(
            level === 1
              ? "px-4 py-2.5 font-medium"
              : "px-3 py-1.5 text-sm font-medium",
            "rounded-md",
          )}
        >
          {itemLabel}
        </div>
      </Tooltip>
    );
  };

  // Create a root item object
  const rootItem: NavItemType = {
    label: label,
    href: href,
    subitems: items,
  };

  // If it has a direct href and no items, it's a simple link
  if (href && !items?.length) {
    return renderNavItem(rootItem, 1, 0);
  }

  // Otherwise, it's a menu that might contain links or other menus
  return renderNavItem(rootItem, 1, 0);
}

export function RoundNavbar({ roundId }: { roundId: string }) {
  const context = useContext(StateContext);
  const { round, actingAsUserId: userId, userProfiles, mode } = context;
  const [location] = useLocation();
  const roundMember = round.members[userId];
  const userProfile = userProfiles[userId];
  const isAdmin = userIsAdmin(userId, context);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const prevLocationRef = useRef<string | null>(null);
  const effectiveTrack = getEffectiveTrack(mode, roundMember);

  // Get actual recommenders and funders for Evaluator MVFs
  const recommenders = getActiveRecommendersWithTrack(round);
  const trackMembers = makeTrackMembers(round);

  const anonymizedRecommenders = recommenders.map((r) => {
    return canView(effectiveTrack, r.track)
      ? r
      : {
          ...r,
          shortname: anonymizeTrackMember(trackMembers, r.memberId)!.shortname,
        };
  });

  const funders = getActiveFunders(round);

  // Get the current user's evaluator (if they are one)
  const currentUserEvaluator = getActiveEvaluators(round).find(
    (evaluator) => evaluator.memberId === userId,
  );

  const userEvalUrlShortname = currentUserEvaluator?.shortname ?? "";

  // Create recommender submenu items
  const recommenderItems = anonymizedRecommenders
    .filter(
      (recommender) =>
        recommender.shortname !== currentUserEvaluator?.shortname,
    )
    .map((r) => {
      return {
        meanRecommenderThreshold: r.meanRecommenderThreshold,
        shortname: r.shortname,
        cantView: !canView(effectiveTrack, r.track),
      };
    })
    .sort(
      sortByMultipleProperties(
        "meanRecommenderThreshold",
        "cantView",
        "shortname",
      ),
    )
    .map((recommender) => {
      let label = recommender.shortname;
      if (recommender.meanRecommenderThreshold !== undefined) {
        label = `µ (${label})`;
      }
      return {
        label,
        href: `~/round/${roundId}/allocations/${recommender.shortname}`,
      };
    });

  // Create funder submenu items
  const funderItems = funders
    .filter((funder) => funder.shortname !== currentUserEvaluator?.shortname)
    .sort(sortByProperty("shortname"))
    .map((funder) => ({
      label: funder.shortname,
      href: `~/round/${roundId}/allocations/${funder.shortname}`,
    }));

  let mainHasMembers = false;
  let freedomHasMembers = false;
  let fairnessHasMembers = false;

  for (const member of Object.values(round.members)) {
    if (member.track === "MAIN") {
      mainHasMembers = true;
    }
    if (member.track === "FREEDOM") {
      freedomHasMembers = true;
    }
    if (member.track === "FAIRNESS") {
      fairnessHasMembers = true;
    }
  }

  const recusalsHref = useMemo(() => {
    if (effectiveTrack === "NO_TRACK") {
      return "";
    } else {
      return `~/round/${roundId}/recusals/${effectiveTrack.toLowerCase()}`;
    }
  }, [effectiveTrack, roundId]);

  const recusalsItems = useMemo(() => {
    if (effectiveTrack === "NO_TRACK") {
      return [
        ["MAIN", "NO_TRACK"].includes(effectiveTrack) &&
          mainHasMembers && {
            label: "Main",
            href: `~/round/${roundId}/recusals/main`,
          },
        ["FREEDOM", "NO_TRACK"].includes(effectiveTrack) &&
          freedomHasMembers && {
            label: "Freedom",
            href: `~/round/${roundId}/recusals/freedom`,
          },
        ["FAIRNESS", "NO_TRACK"].includes(effectiveTrack) &&
          fairnessHasMembers && {
            label: "Fairness",
            href: `~/round/${roundId}/recusals/fairness`,
          },
      ].filter(Boolean) as NavItemType[];
    } else {
      return [];
    }
  }, [
    effectiveTrack,
    roundId,
    mainHasMembers,
    freedomHasMembers,
    fairnessHasMembers,
  ]);

  // Define all navigation items in a structured format
  const navItems = useMemo(
    (): NavItem[] => [
      // TODO SP-535: Add dashboard nav item back in when/if dashboard is ready
      // {
      //   label: "My Dashboard",
      //   href: `~/round/${roundId}/dashboard`,
      //   pathPatterns: ["/dashboard"],
      //   access: {
      //     phases: [
      //       "2.initial-evaluations",
      //       "3.full-discussion",
      //       "4.evals-freeze",
      //     ],
      //   },
      // },
      {
        label: "Onboarding",
        href: `~/round/${roundId}/onboarding`,
        pathPatterns: ["/onboarding"],
        canAccess: getNavItemVisibility({
          phases: accessiblePhases("Onboarding", isUserFunder(round, userId)),
        }),
      },
      {
        label: "Recusals",
        href: recusalsHref,
        pathPatterns: [
          "/recusals/main",
          "/recusals/freedom",
          "/recusals/fairness",
        ],
        items: recusalsItems,
        canAccess: (round, userId) => {
          return getNavItemVisibility({
            phases: accessiblePhases("Recusals", isUserFunder(round, userId)),
          })(round, userId, effectiveTrack);
        },
      } as NavItem,
      {
        label: "Investigations",
        href: `~/round/${roundId}/investigations`,
        pathPatterns: ["/investigations"],
        canAccess: (round, userId) => {
          return getNavItemVisibility({
            phases: accessiblePhases(
              "Investigations",
              isUserFunder(round, userId),
            ),
          })(round, userId, effectiveTrack);
        },
      },
      {
        label: "My Evaluations",
        href: userEvalUrlShortname
          ? `~/round/${roundId}/allocations/${userEvalUrlShortname}`
          : "",
        pathPatterns: userEvalUrlShortname
          ? [`/allocations/${userEvalUrlShortname}`]
          : [],
        canAccess: getNavItemVisibility({
          phases: accessiblePhases(
            "My Evaluations",
            isUserFunder(round, userId),
          ),
          isEvaluator: true,
        }),
      },
      {
        label: userEvalUrlShortname ? "Others’ Evaluations" : "Evaluations",
        pathPatterns: recommenderItems
          .map((i) => i.href.replace(`~/round/${roundId}`, ""))
          .concat(
            funderItems.map((i) => i.href.replace(`~/round/${roundId}`, "")),
          ),
        items: [
          {
            label: "Recommenders",
            isSubmenu: true,
            subitems: recommenderItems,
          },
          {
            label: "Funders",
            isSubmenu: true,
            subitems: funderItems,
          },
        ],
        canAccess: (round, userId) => {
          return getNavItemVisibility({
            phases: accessiblePhases(
              "Others' Evaluations",
              isUserFunder(round, userId),
            ),
          })(round, userId, effectiveTrack);
        },
      },
      {
        label: "Analysis",
        pathPatterns: [
          "/allocations/aggregator/sankey",
          "/disagreements",
          "/comparisons",
          "/allocations/aggregator/recommenders-accountables",
          "/allocations/aggregator/recommenders-guarantees",
          "/allocations/aggregator/funders-accountables",
          "/allocations/aggregator/funders-guarantees",
        ],
        items: [
          {
            label: "Sankey Chart",
            href: `~/round/${roundId}/allocations/aggregator/sankey`,
          },
          { label: "Disagreements", href: `~/round/${roundId}/disagreements` },
          { label: "Comparisons", href: `~/round/${roundId}/comparisons` },
          {
            label: "Recommender",
            isSubmenu: true,
            subitems: [
              {
                label: "Accountables",
                href: `~/round/${roundId}/allocations/aggregator/recommenders-accountables`,
              },
              {
                label: "Guarantees",
                href: `~/round/${roundId}/allocations/aggregator/recommenders-guarantees`,
              },
            ],
          },
          {
            label: "Funder",
            isSubmenu: true,
            subitems: [
              {
                label: "Accountables",
                href: `~/round/${roundId}/allocations/aggregator/funders-accountables`,
              },
              {
                label: "Guarantees",
                href: `~/round/${roundId}/allocations/aggregator/funders-guarantees`,
              },
            ],
          },
        ],
        canAccess: (round, userId) => {
          return getNavItemVisibility({
            phases: accessiblePhases("Analysis", isUserFunder(round, userId)),
          })(round, userId, effectiveTrack);
        },
      },
      {
        label: "Speculation Grants",
        href: `~/round/${roundId}/speculation-grants`,
        pathPatterns: ["/speculation-grants"],
        canAccess: getNavItemVisibility({
          roundNameRestriction: "SFF-2024",
          phases: ["4.evals-freeze", "5.complete"],
        }),
      },
      {
        label: "Settings",
        href: `~/round/${roundId}/settings`,
        pathPatterns: ["/settings"],
        canAccess: getNavItemVisibility({
          phases: [
            "2.initial-evaluations",
            "3.full-discussion",
            "4.evals-freeze",
            "5.complete",
          ],
        }),
      },
      {
        label: "Tools",
        pathPatterns: ["/logs", "/export-data"],
        items: [
          { label: "Logs", href: `~/round/${roundId}/logs` },
          { label: "Import/Export", href: `~/round/${roundId}/export-data` },
        ],
        canAccess: getNavItemVisibility({
          adminOnly: true,
          phases: ["3.full-discussion", "4.evals-freeze", "5.complete"],
        }),
      },
      {
        label: "Admin",
        pathPatterns: [
          "/admin/track-settings/main",
          "/admin/track-settings/fairness",
          "/admin/track-settings/freedom",
          "/admin/round-members",
          "/admin/proposal-forms",
          "/admin/proposals-viewer",
          "/admin/data-syncing",
        ],
        items: [
          {
            label: "Track Settings",
            isSubmenu: true,
            subitems: [
              {
                label: "Main",
                href: `~/round/${roundId}/admin/track-settings/main`,
              },
              {
                label: "Fairness",
                href: `~/round/${roundId}/admin/track-settings/fairness`,
              },
              {
                label: "Freedom",
                href: `~/round/${roundId}/admin/track-settings/freedom`,
              },
            ],
          },
          {
            label: "Round Members",
            href: `~/round/${roundId}/admin/round-members`,
          },
          {
            label: "Proposal Forms",
            href: `~/round/${roundId}/admin/proposal-forms`,
          },
          {
            label: "Proposals Viewer",
            href: `~/round/${roundId}/admin/proposals-viewer`,
          },
          {
            label: "Data Syncing",
            href: `~/round/${roundId}/admin/data-syncing`,
          },
        ],
        canAccess: getNavItemVisibility({
          adminOnly: true,
          phases: allPhases,
        }),
      },
    ],
    [
      roundId,
      userEvalUrlShortname,
      recommenderItems,
      funderItems,
      recusalsHref,
      recusalsItems,
      effectiveTrack,
      round,
      userId,
    ],
  );

  // Close menus on navigation unless the current page is in that menu
  useEffect(() => {
    // // Only update the active menu if the location has actually changed
    if (prevLocationRef.current === location) {
      return;
    }

    // Extract just the path part for easier matching
    const currentPath = location.startsWith("/round/")
      ? "/" + location.split("/", 3).slice(2).join("/")
      : location;

    // Update the ref with the current location
    prevLocationRef.current = location;

    // If a menu is already open, check if we should keep it open
    if (activeSubmenu) {
      const navItem = navItems.find((item) => item.label === activeSubmenu);
      if (
        navItem &&
        navItem.pathPatterns.some(
          (pattern) =>
            currentPath === pattern || currentPath.startsWith(pattern + "/"),
        )
      ) {
        // Keep the menu open
        return;
      }
    }

    // Check which menu should be open based on the current location
    for (const item of navItems) {
      if (
        item.items &&
        item.pathPatterns.some(
          (pattern) =>
            currentPath === pattern || currentPath.startsWith(pattern + "/"),
        )
      ) {
        setActiveSubmenu(item.label);
        return;
      }
    }

    // Default: close all menus
    setActiveSubmenu(null);
  }, [location, navItems, activeSubmenu]);

  const tracksToShow = getTracksToShow(round, roundMember, effectiveTrack);

  // Function to check if an item should be shown (visible to any user)
  const shouldShowNavItem = (item: NavItem): boolean => {
    const visibility = item.canAccess(round, userId, effectiveTrack).visible;
    return (
      visibility === "visible" ||
      visibility === "disabled-for-non-admins" ||
      (visibility === "hidden-from-non-admins" && isAdmin)
    );
  };

  return (
    <div
      className={clsx(
        "h-full transition-all duration-300 ease-in-out",
        "text-gray-600 dark:text-gray-400 bg-stone-200 dark:bg-stone-800",
        "w-auto min-w-[200px] max-w-[400px]",
      )}
    >
      <div className="h-full flex flex-col">
        <div className="px-4 py-3 border-b border-stone-300 dark:border-stone-700">
          <RoundTitle />
          <div className="text-sm text-stone-500 dark:text-stone-400 mt-1">
            <ul>
              {tracksToShow.map((t) => (
                <li key={t}>
                  {trackNames[t]}:{" "}
                  {getRoundStatusDisplayName(round.trackSettings[t].status)}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="flex flex-col px-2 space-y-1 grow overflow-y-auto py-2">
          {navItems
            .filter((item) => shouldShowNavItem(item))
            .map((item, index) => (
              <NavigationItem
                key={index}
                label={item.label}
                href={item.href}
                items={item.items}
                roundId={roundId}
                activeSubmenu={activeSubmenu}
                setActiveSubmenu={setActiveSubmenu}
                visibility={item.canAccess(round, userId, effectiveTrack)}
                isAdmin={isAdmin}
              />
            ))}
        </div>

        <div className="p-4 flex justify-center">
          <SocketStatusDisplay />

          <ThemeSelector />
        </div>

        <div className="p-4 flex flex-col justify-center gap-2">
          {roundMember && (
            <div>
              Logged in as{" "}
              {userProfile ? (
                <Tooltip title={userProfile.email}>{userProfile.name}</Tooltip>
              ) : (
                roundMember.initials
              )}
            </div>
          )}
          <a href="/" className="text-sm">
            &larr; S-Process Home
          </a>
        </div>
      </div>
    </div>
  );
}

function explanationOfDisconnectReason(reason: IoSocket.DisconnectReason) {
  switch (reason) {
    case "io client disconnect":
      return "The socket was manually disconnected by the client. This should never happen -- please report it to S-Process admins.";
    case "io server disconnect":
      return "The server forcefully disconnected the socket. This should never happen -- please report it to S-Process admins.";
    case "ping timeout":
      return "The server did not respond to a ping. Maybe it's down?";
    case "transport close":
      return "The connection was closed. Did you lose your internet connection or change networks?";
    case "transport error":
      return "The connection encountered an error.";
    case "parse error":
      Sentry.captureException(reason);
      return "The connection received an invalid message. This should never happen -- please report it to S-Process admins.";
    default:
      assertUnreachable(reason);
  }
}

function SocketStatusDisplay() {
  const {
    socketStatus,
    unconfirmedActions: unconfirmedActionsPromise,
    retryUnconfirmedActions,
    clearUnconfirmedActions,
    reportError,
    storage,
    round,
  } = useContext(StateContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [unconfirmedActions, setUnconfirmedActions] = useState<
    {
      createdAt: number;
      message: UnconfirmedActionMessage;
    }[]
  >([]);
  const [actionsAreTooOld, setActionsAreTooOld] = useState(false);
  const timerRef = useRef<Timer | null>(null);
  const renderCount = useRef(0);

  renderCount.current++;

  function reportUnconfirmedActionsAreTooOld() {
    setActionsAreTooOld(true);
    Sentry.captureMessage(`Unconfirmed actions are older than 5 seconds`, {
      level: "warning",
    });
  }
  useEffect(() => {
    void unconfirmedActionsPromise.then((acts) => {
      setUnconfirmedActions(acts);
      if (timerRef.current != null) {
        clearTimeout(timerRef.current);
      }
      if (acts.length > 0) {
        const now = new Date();
        const oldestAge = now.getTime() - acts[0].createdAt;
        if (oldestAge > 5_000) {
          reportUnconfirmedActionsAreTooOld();
        } else {
          timerRef.current = setTimeout(
            reportUnconfirmedActionsAreTooOld,
            5_000 - oldestAge,
          );
        }
      }
    });
  }, [unconfirmedActionsPromise, storage]);

  let explanation: React.ReactNode = "";
  let socketStatusIcon: React.ReactNode;
  if (socketStatus === "connected") {
    explanation = "connected";

    if (unconfirmedActions.length === 0) {
      socketStatusIcon = <CloudDoneOutline className="text-2xl" />;
    } else if (actionsAreTooOld) {
      socketStatusIcon = (
        <CloudAlertOutline className="text-red-500 text-2xl" />
      );
    } else {
      socketStatusIcon = <CloudSyncOutline className="text-2xl" />;
    }
  } else if (socketStatus === null) {
    socketStatusIcon = <CloudOffOutline className="text-yellow-400 text-2xl" />;
    explanation = "unknown";
  } else {
    socketStatusIcon = <CloudOffOutline className="text-2xl" />;
    let description = explanationOfDisconnectReason(socketStatus.reason);
    if (
      socketStatus.description &&
      !(socketStatus.description instanceof Error)
    ) {
      description = socketStatus.description.description;
    }

    explanation = (
      <div className="flex flex-col gap-2">
        <div>{description}</div>
        <div>
          {socketStatus.willReconnect
            ? "We will reconnect when back online"
            : "We will not reconnect"}
        </div>
      </div>
    );
  }

  return (
    <div>
      <Tooltip
        key={renderCount.current}
        placement="bottom-start"
        className="m-2"
        clickable
        titleClassName="bg-stone-100 dark:bg-stone-800 p-2 rounded-lg flex flex-col gap-2"
        title={
          <>
            <p className="font-bold">Connection Status</p>
            <div className="text-sm">{explanation}</div>
            {unconfirmedActions.length === 0 && (
              <div>All actions are synced with the server</div>
            )}
            {unconfirmedActions.length > 0 && (
              <button
                className="text-red-500 cursor-pointer"
                onClick={() => setModalOpen(true)}
              >
                {unconfirmedActions.length === 1
                  ? "See one pending action"
                  : `See ${unconfirmedActions.length} pending actions`}
              </button>
            )}
          </>
        }
      >
        {socketStatusIcon}
      </Tooltip>
      {modalOpen && (
        <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
          <div className="flex flex-col gap-2 max-h-[90vh] min-w-[400px] max-w-[80vw] overflow-y-auto">
            <div>Pending Actions</div>
            <div className="flex flex-row gap-2">
              <Button onClick={() => void retryUnconfirmedActions()}>
                Retry Actions
              </Button>
              <Button
                onClick={() => {
                  setModalOpen(false);
                  void clearUnconfirmedActions();
                }}
              >
                Clear Actions
              </Button>
              <Button
                onClick={async () => {
                  await reportError();
                  toast.success("Error reported");
                }}
              >
                Report Error
              </Button>
            </div>
            <div className="border">
              {unconfirmedActions.map(({ message }, i) => {
                const actionMessage: ActionMessage = {
                  ...message,
                  version: i,
                  timestamp: new Date().toISOString(),
                };
                return (
                  <ShowEventLogItem
                    key={message.id}
                    item={actionMessage}
                    round={round}
                  />
                );
              })}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Function to determine a navItem's visibility status
const getNavItemVisibility =
  (access: NavItemAccess) =>
  (
    round: RoundAggregate,
    userId: string,
    effectiveTrack: Track,
  ): NavItemVisibility => {
    const roundMember = round.members[userId];
    // Check admin restriction
    if (access.adminOnly) {
      return {
        visible: "hidden-from-non-admins",
        tooltip: "This section is only visible to admins",
      };
    }

    // Check if isEvaluator is specified and user needs to be an evaluator
    if ("isEvaluator" in access && access.isEvaluator === true) {
      if (!isUserRecommenderOrFunder(round, userId))
        return {
          visible: "hidden-from-non-admins",
          tooltip: "This section is only visible to evaluators",
        };
    }

    // Check round name restriction (only apply if specified)
    if (
      "roundNameRestriction" in access &&
      access.roundNameRestriction &&
      access.roundNameRestriction !== round.name
    ) {
      return { visible: "hidden-for-all" };
    }
    // Check if item should be visible based on phase
    const phases = access.phases ?? [];
    const tracksToShow = getTracksToShow(round, roundMember, effectiveTrack);
    const currentPhases = tracksToShow.map(
      (track) => round.trackSettings[track].status,
    );
    if (currentPhases.some((phase) => phases.includes(phase))) {
      return { visible: "visible" };
    } else {
      // Item would normally be hidden, but shown to admins with special styling
      return {
        visible: "disabled-for-non-admins",
        tooltip: phaseRestrictedTooltip(phases, currentPhases),
      };
    }
  };
